import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type SetStateAction,
} from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type OnSelectionChangeParams,
  useReactFlow,
  ReactFlowProvider,
  ConnectionMode,
  useUpdateNodeInternals,
  type EdgeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  resolveStatuses,
  STATUS_COLORS,
  STATUS_LABELS_UK,
  DEVICE_TYPE_LABELS_UK,
  DIAGRAM_GRID_SIZE,
  buildOccupiedPortMap,
  deviceSupportsPorts,
  deviceSupportsWifiAntennas,
  displayPortId,
  ETHERNET_PORT_ID,
  firstFreeAttachmentId,
  isLogicalEthernetPort,
  isWifiPortId,
  resolvePortCount,
  resolvePortLayout,
  resolveWifiAntennaCount,
  snapPosition,
  snapWithPeerAlign,
  suggestSizeForPorts,
  type AlignGuide,
  type DeviceType,
  type DiagramEdge,
  type DiagramNode,
  type NodeStatus,
  normalizeNetworkLifecycle,
} from '@ukrop-nm/shared';
import { Copy, History, Info, LayoutGrid, Pencil, Trash2 } from 'lucide-react';
import { Palette } from './Palette';
import { EditorRibbon } from './EditorRibbon';
import { KandoMenu, type KandoItem } from './KandoMenu';
import { EdgeEditModal, NodeEditModal } from './PropsEditModal';
import { DeviceInfoModal } from './DeviceInfoModal';
import { AlignGuidesOverlay } from './AlignGuidesOverlay';
import { nodeTypes } from './nodeTypes';
import { edgeTypes } from './edgeTypes';
import { useDiagram, useDiagramVersions } from '../hooks/useDiagram';
import { diagramStore } from '../state/diagramStore';
import { buildScenario, type SimulatorState } from '../simulator/scenarios';
import { refreshAppearanceFromNameModel } from '../device/deviceAppearance';
import { PortHoverProvider } from '../edges/PortHoverContext';
import { EdgeRoutingProvider } from '../edges/EdgeRoutingContext';
import {
  exportCsvZip,
  exportDiagramJson,
  exportDrawio,
  exportProjectPdf,
  exportProjectZip,
  exportTopologyJpeg,
  exportTopologySvg,
} from '../export/exportNetworkProject';
import { loadDrawingMeta, saveDrawingMeta, type DrawingSheetMeta } from '../export/drawingMeta';
import { unzipSync, strFromU8 } from 'fflate';
import { DrawingMetaModal } from './DrawingMetaModal';
import { SaveProjectModal, type SaveProjectValues } from './SaveProjectModal';
import { DiagramZoomControls } from './DiagramZoomControls';
import { useDiagramChrome } from '../layout/DiagramChromeContext';
import { scheduleDiagramViewport, defaultViewportForOpenZoom } from './diagramViewport';
import { loadUserUiSettings } from '../auth/userSettings';
import { SETTINGS_CHANGED_EVENT } from '../auth/accountScope';

function nodeFlowSize(n: DiagramNode): { width: number; height: number } {
  if (n.type === 'zone') {
    return { width: n.size?.width ?? 320, height: n.size?.height ?? 220 };
  }
  return suggestSizeForPorts(
    resolvePortCount(n),
    n.size,
    resolvePortLayout(n),
    resolveWifiAntennaCount(n),
  );
}

function toFlowNodes(
  nodes: DiagramNode[],
  edges: DiagramEdge[],
  statusByKey: Map<string, NodeStatus>,
  editable = true,
): Node[] {
  const occupiedMap = buildOccupiedPortMap(edges);
  return nodes.map((n) => {
    const size = nodeFlowSize(n);
    return {
      id: n.id,
      type: 'ukrop',
      position: n.position,
      className: n.type === 'zone' ? 'ukrop-zone-node' : 'ukrop-device-node',
      zIndex: n.type === 'zone' ? -2 : 3,
      style:
        n.type === 'zone'
          ? { width: size.width, height: size.height, zIndex: -2 }
          : { width: size.width, height: size.height, zIndex: 3 },
      data: {
        label: n.label,
        deviceType: n.type,
        locked: n.locked,
        monitorKey: n.monitorKey,
        status: statusByKey.get(n.monitorKey || n.id),
        color: n.color,
        portCount: n.portCount ?? resolvePortCount(n),
        portLayout: n.portLayout ?? resolvePortLayout(n),
        wifiAntennaCount: n.wifiAntennaCount ?? resolveWifiAntennaCount(n),
        portProfileId: n.portProfileId,
        interfaces: n.interfaces,
        occupiedPorts: [...(occupiedMap.get(n.id) ?? [])],
        editable,
      },
      draggable: !n.locked && editable,
      selectable: true,
    };
  });
}

function flowHandleId(port?: string | null): string | undefined {
  const p = displayPortId(port);
  // Logical placeholder has no DOM handle — let RF attach to the first real port
  if (isLogicalEthernetPort(p)) return undefined;
  return p;
}

function toFlowEdges(
  edges: DiagramEdge[],
  linkMap: Map<string, { status: NodeStatus; evidence: string }>,
  editable: boolean,
): Edge[] {
  return edges.map((e) => {
    const link = linkMap.get(e.id);
    return {
      id: e.id,
      source: e.sourceNodeId,
      target: e.targetNodeId,
      sourceHandle: flowHandleId(e.sourcePort),
      targetHandle: flowHandleId(e.targetPort),
      type: 'ukrop',
      zIndex: 0,
      data: {
        label: e.label,
        status: link?.status,
        evidence: link?.evidence,
        monitorSource: e.monitorSource,
        connectionType: e.connectionType,
        color: e.color,
        sourcePort: e.sourcePort,
        targetPort: e.targetPort,
        labelT: e.labelT,
        routeOffset: e.routeOffset,
        editable,
      },
    };
  });
}

type KandoState =
  | { kind: 'node'; id: string; x: number; y: number }
  | { kind: 'edge'; id: string; x: number; y: number }
  | { kind: 'canvas'; x: number; y: number }
  | null;

type EditTarget =
  | { kind: 'node'; id: string }
  | { kind: 'edge'; id: string }
  | null;

type InfoTarget =
  | { kind: 'node'; id: string }
  | { kind: 'edge'; id: string }
  | null;

function EditorInner({
  mode,
  setMode,
  sim,
  setSim,
}: {
  mode: 'edit' | 'monitor';
  setMode: (m: 'edit' | 'monitor') => void;
  sim: SimulatorState;
  setSim: Dispatch<SetStateAction<SimulatorState>>;
}) {
  const diagram = useDiagram();
  const versions = useDiagramVersions();
  const { screenToFlowPosition, setCenter, getZoom, fitView, getNodes } = useReactFlow();
  const viewportAppliedRef = useRef('');
  const updateNodeInternals = useUpdateNodeInternals();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [sanitization, setSanitization] = useState<string[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [kando, setKando] = useState<KandoState>(null);
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [infoTarget, setInfoTarget] = useState<InfoTarget>(null);
  const [alignGuides, setAlignGuides] = useState<AlignGuide[]>([]);
  const [nodeDragging, setNodeDragging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drawingMetaOpen, setDrawingMetaOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [pendingExport, setPendingExport] = useState<'jpeg' | 'pdf' | 'zip' | null>(null);
  const chromeApi = useDiagramChrome();
  const dragSnapAt = useRef(0);

  const resolved = useMemo(
    () =>
      resolveStatuses({
        nowMs: Date.now(),
        lastHeartbeatAtMs: sim.lastHeartbeatAtMs,
        payload: sim.payload,
        nodes: diagram.nodes,
        edges: diagram.edges,
      }),
    [diagram, sim],
  );

  const statusByKey = useMemo(() => {
    const map = new Map<string, NodeStatus>();
    for (const n of resolved.nodes) map.set(n.monitorKey, n.status);
    return map;
  }, [resolved.nodes]);

  const linkMap = useMemo(() => {
    const map = new Map<string, { status: NodeStatus; evidence: string }>();
    for (const l of resolved.links) {
      map.set(l.edgeId, { status: l.status, evidence: l.evidence });
    }
    return map;
  }, [resolved.links]);

  const [nodes, setNodes, onNodesChange] = useNodesState(
    toFlowNodes(diagram.nodes, diagram.edges, statusByKey, mode === 'edit'),
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    toFlowEdges(diagram.edges, linkMap, mode === 'edit'),
  );

  // RF may emit "remove" when a handle id is briefly missing after port edits — keep edges in store
  const onEdgesChangeSafe = useCallback(
    (changes: EdgeChange[]) => {
      const safe = changes.filter((c) => c.type !== 'remove');
      if (safe.length) onEdgesChange(safe);
    },
    [onEdgesChange],
  );

  useEffect(() => {
    setNodes(toFlowNodes(diagram.nodes, diagram.edges, statusByKey, mode === 'edit'));
    setEdges(toFlowEdges(diagram.edges, linkMap, mode === 'edit'));
    // Remeasure port handles so Eth ↔ Wi‑Fi switches keep edge endpoints
    for (const n of diagram.nodes) {
      if (n.type !== 'zone') updateNodeInternals(n.id);
    }
  }, [diagram, statusByKey, linkMap, mode, setNodes, setEdges, updateNodeInternals]);

  const diagramOpenZoom = loadUserUiSettings().diagramOpenZoom;
  const flowDefaultViewport = useMemo(
    () => defaultViewportForOpenZoom(diagramOpenZoom),
    [diagram.siteId, diagramOpenZoom],
  );

  /** Apply saved opening zoom once per site + mode (uses document geometry, not DOM measure). */
  useEffect(() => {
    const key = `${diagram.siteId}:${mode}`;
    if (viewportAppliedRef.current === key) return;

    const cancel = scheduleDiagramViewport(
      { fitView, setCenter },
      diagramOpenZoom,
      diagram.nodes,
      () => {
        viewportAppliedRef.current = key;
      },
    );

    return cancel;
  }, [diagram.siteId, mode, diagramOpenZoom, fitView, setCenter, diagram.nodes]);

  useEffect(() => {
    viewportAppliedRef.current = '';
  }, [diagram.siteId, mode]);

  useEffect(() => {
    const onSettings = () => {
      viewportAppliedRef.current = '';
      const zoom = loadUserUiSettings().diagramOpenZoom;
      scheduleDiagramViewport({ fitView, setCenter }, zoom, diagram.nodes, () => {
        viewportAppliedRef.current = `${diagram.siteId}:${mode}`;
      });
    };
    window.addEventListener(SETTINGS_CHANGED_EVENT, onSettings);
    return () => window.removeEventListener(SETTINGS_CHANGED_EVENT, onSettings);
  }, [diagram.siteId, diagram.nodes, mode, fitView, setCenter]);

  useEffect(() => {
    if (mode !== 'edit') setPaletteOpen(false);
  }, [mode]);

  useEffect(() => {
    const onAdd = (ev: Event) => {
      if (mode !== 'edit') return;
      const detail = (ev as CustomEvent).detail as {
        type: DeviceType;
        position: { x: number; y: number };
      };
      diagramStore.addDevice(detail.type, snapPosition(detail.position));
    };
    const onDropDevice = (ev: Event) => {
      if (mode !== 'edit') return;
      const detail = (ev as CustomEvent).detail as {
        type: DeviceType;
        clientX: number;
        clientY: number;
      };
      if (!detail?.type) return;
      diagramStore.addDevice(
        detail.type,
        snapPosition(screenToFlowPosition({ x: detail.clientX, y: detail.clientY })),
      );
      setPaletteOpen(false);
    };
    window.addEventListener('ukrop-add-device', onAdd);
    window.addEventListener('ukrop-drop-device', onDropDevice);
    return () => {
      window.removeEventListener('ukrop-add-device', onAdd);
      window.removeEventListener('ukrop-drop-device', onDropDevice);
    };
  }, [mode, screenToFlowPosition]);

  const onConnect = useCallback(
    (connection: Connection) => {
      if (mode !== 'edit' || !connection.source || !connection.target) return;
      const snap = diagramStore.getSnapshot();
      const sourceNode = snap.nodes.find((n) => n.id === connection.source);
      const targetNode = snap.nodes.find((n) => n.id === connection.target);
      const occupied = buildOccupiedPortMap(snap.edges);

      let sourcePort = connection.sourceHandle ?? undefined;
      let targetPort = connection.targetHandle ?? undefined;

      if (!sourcePort && sourceNode) {
        sourcePort =
          firstFreeAttachmentId(sourceNode, occupied.get(sourceNode.id) ?? new Set()) ??
          ETHERNET_PORT_ID;
      }
      if (!targetPort && targetNode) {
        const busy = new Set(occupied.get(targetNode.id) ?? []);
        if (sourcePort && connection.source === connection.target) busy.add(sourcePort);
        targetPort = firstFreeAttachmentId(targetNode, busy) ?? ETHERNET_PORT_ID;
      }
      sourcePort = sourcePort || ETHERNET_PORT_ID;
      targetPort = targetPort || ETHERNET_PORT_ID;

      const wifiLink = isWifiPortId(sourcePort) || isWifiPortId(targetPort);

      const edge: DiagramEdge = {
        id: crypto.randomUUID(),
        sourceNodeId: connection.source,
        targetNodeId: connection.target,
        sourcePort,
        targetPort,
        connectionType: wifiLink ? 'wifi' : 'ethernet',
        monitorSource: 'unknown',
        criticality: 'normal',
        label: '',
      };
      diagramStore.updateEdges((eds) => [...eds, edge]);
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: edge.id,
            type: 'ukrop',
            sourceHandle: flowHandleId(sourcePort),
            targetHandle: flowHandleId(targetPort),
          },
          eds,
        ),
      );
    },
    [mode, setEdges],
  );

  const onNodeDragStart = useCallback(() => {
    setNodeDragging(true);
  }, []);

  const onNodeDrag = useCallback(
    (_: unknown, node: Node) => {
      if (mode !== 'edit') return;
      const now = performance.now();
      // Peer-align guides ~30fps — full edge re-route stays in fast mode while dragging
      if (now - dragSnapAt.current < 32) return;
      dragSnapAt.current = now;

      const w =
        Number(node.measured?.width) ||
        Number(node.width) ||
        (typeof node.style?.width === 'number' ? node.style.width : 0) ||
        160;
      const h =
        Number(node.measured?.height) ||
        Number(node.height) ||
        (typeof node.style?.height === 'number' ? node.style.height : 0) ||
        144;
      const peers = nodes
        .filter((n) => {
          if (n.id === node.id) return false;
          if (n.className === 'ukrop-zone-node') return false;
          return (n.data as { deviceType?: string } | undefined)?.deviceType !== 'zone';
        })
        .map((n) => ({
          id: n.id,
          x: n.position.x,
          y: n.position.y,
          width:
            Number(n.measured?.width) ||
            Number(n.width) ||
            (typeof n.style?.width === 'number' ? n.style.width : 0) ||
            160,
          height:
            Number(n.measured?.height) ||
            Number(n.height) ||
            (typeof n.style?.height === 'number' ? n.style.height : 0) ||
            144,
        }));
      const { position, guides } = snapWithPeerAlign(
        node.id,
        node.position,
        { width: w, height: h },
        peers,
      );
      setAlignGuides(guides);
      if (position.x !== node.position.x || position.y !== node.position.y) {
        setNodes((list) => list.map((n) => (n.id === node.id ? { ...n, position } : n)));
      }
    },
    [mode, nodes, setNodes],
  );

  const onNodeDragStop = useCallback(
    (_: unknown, node: Node) => {
      if (mode !== 'edit') return;
      setNodeDragging(false);
      setAlignGuides([]);
      const snapped = snapPosition(node.position);
      setNodes((list) => list.map((n) => (n.id === node.id ? { ...n, position: snapped } : n)));
      diagramStore.updateNodes(
        (list) =>
          list.map((n) => (n.id === node.id ? { ...n, position: snapped } : n)),
        true,
      );
    },
    [mode, setNodes],
  );

  const onSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    setSelectedIds([...params.nodes.map((n) => n.id), ...params.edges.map((e) => e.id)]);
  }, []);

  const saveMeta = loadDrawingMeta(diagram.siteId, diagram.name || diagram.siteId);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const n of resolved.nodes) c[n.status] = (c[n.status] ?? 0) + 1;
    return c;
  }, [resolved.nodes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return diagram.nodes.filter(
      (n) => n.label.toLowerCase().includes(q) || n.monitorKey.toLowerCase().includes(q),
    );
  }, [diagram.nodes, query]);

  const save = () => {
    setSaveModalOpen(true);
  };

  const confirmSave = (values: SaveProjectValues) => {
    try {
      const meta = loadDrawingMeta(diagram.siteId, values.name);
      saveDrawingMeta(diagram.siteId, {
        ...meta,
        projectName: values.name,
        objectName: meta.objectName?.trim() || values.name,
        purposeGoal: values.purposeGoal,
        purposeAudience: values.purposeAudience,
        purposeScope: values.purposeScope,
      });
      diagramStore.applySaveFields(values.name, values.lifecycle);
      setSaveModalOpen(false);
      setSaveBusy(false);
      // Close first; stringify + PUT must not freeze the dialog.
      window.setTimeout(() => {
        diagramStore.flushSave();
        setSanitization(diagramStore.getSanitizationReport());
      }, 0);
    } catch (err) {
      setSaveBusy(false);
      window.alert('Не вдалося зберегти. Спробуйте ще раз.');
    }
  };

  const exportJson = () => {
    exportDiagramJson(diagramStore.getSnapshot());
  };

  const exportDrawioFile = () => {
    exportDrawio(diagramStore.getSnapshot());
  };

  const exportCsv = () => {
    exportCsvZip(diagramStore.getSnapshot());
  };

  const waitForCanvasCapture = async () => {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        window.setTimeout(() => resolve(), 80);
      });
    });
    fitView({
      padding: 0.18,
      duration: 0,
      includeHiddenNodes: true,
      minZoom: 0.05,
      maxZoom: 1,
    });
    await new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), 120);
    });
  };

  const requestDrawingExport = (kind: 'jpeg' | 'pdf' | 'zip') => {
    setPendingExport(kind);
    setDrawingMetaOpen(true);
  };

  const runExport = async (kind: 'jpeg' | 'pdf' | 'zip', meta: DrawingSheetMeta) => {
    setKando(null);
    setExporting(true);
    try {
      await waitForCanvasCapture();
      const doc = diagramStore.getSnapshot();
      saveDrawingMeta(doc.siteId, meta);
      const captureNodes = getNodes();
      if (!captureNodes.length) {
        throw new Error('На схемі немає блоків для знімка.');
      }
      if (kind === 'jpeg') await exportTopologyJpeg(doc, captureNodes, meta);
      if (kind === 'pdf') await exportProjectPdf(doc, captureNodes, meta);
      if (kind === 'zip') await exportProjectZip(doc, captureNodes, meta);
    } catch (err) {
      window.alert(
        `Не вдалося експортувати: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setExporting(false);
    }
  };

  const runSvgExport = async () => {
    setExporting(true);
    try {
      await waitForCanvasCapture();
      const doc = diagramStore.getSnapshot();
      const captureNodes = getNodes();
      if (!captureNodes.length) {
        throw new Error('На схемі немає блоків для знімка.');
      }
      await exportTopologySvg(doc, captureNodes);
    } catch (err) {
      window.alert(
        `Не вдалося експортувати SVG: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setExporting(false);
    }
  };

  const onImportDrawio = async (file: File) => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const warnings = diagramStore.importDrawioBytes(bytes, file.name);
    setImportWarnings(warnings.map((w) => w.message));
    setSanitization(diagramStore.getSanitizationReport());
  };

  const onImportCsv = async (file: File) => {
    try {
      const name = file.name.toLowerCase();
      let files: Array<{ name: string; text: string }>;
      if (name.endsWith('.zip')) {
        const unzipped = unzipSync(new Uint8Array(await file.arrayBuffer()));
        files = Object.entries(unzipped)
          .filter(([path]) => path.toLowerCase().endsWith('.csv') && !path.split(/[/\\]/).some((p) => p.startsWith('.')))
          .map(([path, data]) => ({ name: path, text: strFromU8(data) }));
        if (!files.length) {
          throw new Error('У ZIP немає equipment.csv / links.csv');
        }
      } else {
        files = [{ name: file.name, text: await file.text() }];
      }
      const warnings = diagramStore.importCsvFiles(files);
      setImportWarnings(warnings);
      setSanitization(diagramStore.getSanitizationReport());
    } catch (err) {
      window.alert(`Не вдалося імпортувати CSV: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const onImportJson = async (file: File) => {
    try {
      const text = await file.text();
      diagramStore.importJsonText(text);
      setImportWarnings([]);
      setSanitization(diagramStore.getSanitizationReport());
    } catch (err) {
      window.alert(`Не вдалося імпортувати JSON: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const focusNode = useCallback((id: string) => {
    const node = diagram.nodes.find((n) => n.id === id);
    if (!node) return;
    setNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === id })));
    setSelectedIds([id]);
    setCenter(node.position.x + 40, node.position.y + 40, { zoom: Math.max(getZoom(), 1), duration: 280 });
    setQuery('');
  }, [diagram.nodes, getZoom, setCenter, setNodes]);

  const setChrome = chromeApi?.setChrome;
  useEffect(() => {
    if (!setChrome) return;
    setChrome({
      diagramName: diagram.name,
      scenario: sim.scenario,
      onScenario: (scenario) => setSim((prev) => buildScenario(scenario, prev)),
      query,
      onQueryChange: setQuery,
      searchResults: filtered.map((node) => ({
        id: node.id,
        label: node.label,
        monitorKey: node.monitorKey,
      })),
      onPickSearch: focusNode,
    });
  }, [diagram.name, filtered, focusNode, query, setChrome, setSim, sim.scenario]);

  useEffect(() => {
    return () => setChrome?.(null);
  }, [setChrome]);

  const openKando = (
    kind: 'node' | 'edge' | 'canvas',
    id: string | null,
    e: ReactMouseEvent | MouseEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const x = e.clientX;
    const y = e.clientY;
    // Defer so the browser can paint the menu without waiting on a heavy re-render
    requestAnimationFrame(() => {
      if (kind === 'canvas') {
        setKando({ kind: 'canvas', x, y });
        return;
      }
      if (!id) return;
      setKando({ kind, id, x, y });
    });
  };

  const kandoItems: KandoItem[] = useMemo(() => {
    if (!kando) return [];
    if (kando.kind === 'canvas') {
      return [
        {
          id: 'palette',
          label: 'Палітра',
          icon: <LayoutGrid size={16} />,
          disabled: mode !== 'edit',
        },
        { id: 'versions', label: 'Версії', icon: <History size={16} /> },
      ];
    }
    const canEdit = mode === 'edit';
    return [
      { id: 'info', label: 'Інформація', icon: <Info size={16} /> },
      { id: 'edit', label: 'Редагувати', icon: <Pencil size={16} /> },
      {
        id: 'copy',
        label: 'Копія',
        icon: <Copy size={16} />,
        disabled: !canEdit || kando.kind !== 'node',
      },
      {
        id: 'delete',
        label: 'Видалити',
        icon: <Trash2 size={16} />,
        disabled: !canEdit,
        danger: true,
      },
    ];
  }, [kando, mode]);

  const onKandoSelect = (action: string) => {
    const current = kando;
    setKando(null);
    if (!current) return;

    if (current.kind === 'canvas') {
      if (action === 'palette' && mode === 'edit') setPaletteOpen(true);
      if (action === 'versions') setVersionsOpen(true);
      return;
    }

    if (action === 'info') {
      setInfoTarget({ kind: current.kind, id: current.id });
      return;
    }
    if (action === 'edit') {
      setMode('edit');
      setEditTarget({ kind: current.kind, id: current.id });
      return;
    }
    if (action === 'copy' && current.kind === 'node') {
      diagramStore.duplicateSelected([current.id]);
      return;
    }
    if (action === 'delete') {
      diagramStore.deleteSelected([current.id]);
    }
  };

  const editingNode =
    editTarget?.kind === 'node' ? diagram.nodes.find((n) => n.id === editTarget.id) : undefined;
  const editingEdge =
    editTarget?.kind === 'edge' ? diagram.edges.find((e) => e.id === editTarget.id) : undefined;

  const infoNode =
    infoTarget?.kind === 'node' ? diagram.nodes.find((n) => n.id === infoTarget.id) : undefined;
  const infoEdge =
    infoTarget?.kind === 'edge' ? diagram.edges.find((e) => e.id === infoTarget.id) : undefined;
  const infoResolvedNode = infoNode
    ? resolved.nodes.find((n) => n.monitorKey === (infoNode.monitorKey || infoNode.id))
    : undefined;
  const infoTargetTelemetry = infoNode
    ? (sim.payload?.targets.find((t) => t.monitorKey === infoNode.monitorKey) ?? null)
    : null;
  const infoIface =
    infoNode || infoEdge
      ? (sim.payload?.interfaces.find(
          (i) =>
            i.monitorKey ===
            (infoNode?.monitorKey || infoEdge?.monitorKey || ''),
        ) ?? null)
      : null;

  return (
    <div className="editor-layout">
      <Palette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      <div className="editor-main">
        <EditorRibbon
          mode={mode}
          setMode={setMode}
          canEdit={mode === 'edit'}
          onSave={save}
          onUndo={() => diagramStore.undo()}
          onRedo={() => diagramStore.redo()}
          onDelete={() => diagramStore.deleteSelected(selectedIds)}
          onCopy={() => diagramStore.duplicateSelected(selectedIds)}
          onAlignLeft={() => diagramStore.alignSelected(selectedIds, 'left')}
          onAlignTop={() => diagramStore.alignSelected(selectedIds, 'top')}
          onExportJson={exportJson}
          onExportJpeg={() => requestDrawingExport('jpeg')}
          onExportSvg={() => void runSvgExport()}
          onExportPdf={() => requestDrawingExport('pdf')}
          onExportDrawio={exportDrawioFile}
          onExportCsv={exportCsv}
          onGenerateProject={() => requestDrawingExport('zip')}
          exporting={exporting}
          lifecycle={normalizeNetworkLifecycle(diagram.lifecycle)}
          onLifecycle={(value) => diagramStore.setLifecycle(value)}
          onImportDrawio={(file) => void onImportDrawio(file)}
          onImportJson={(file) => void onImportJson(file)}
          onImportCsv={(file) => void onImportCsv(file)}
          paletteOpen={paletteOpen}
          onTogglePalette={() => setPaletteOpen((v) => !v)}
          onOpenVersions={() => setVersionsOpen(true)}
        />

        <div className="canvas-wrap">
          {(exporting || importWarnings.length > 0 || sanitization.length > 0) && (
            <div className="notice absolute top-2 right-2 left-2 z-[4]">
              {exporting ? <div>Готується файл проєкту…</div> : null}
              {importWarnings.map((w) => (
                <div key={w}>Імпорт: {w}</div>
              ))}
              {sanitization.map((s) => (
                <div key={s}>Очищення: {s}</div>
              ))}
            </div>
          )}

          <EdgeRoutingProvider nodes={nodes} edges={edges} fast={nodeDragging}>
          <ReactFlow
            key={`${diagram.siteId}:${mode}`}
            defaultViewport={flowDefaultViewport}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={mode === 'edit' ? onNodesChange : undefined}
            onEdgesChange={mode === 'edit' ? onEdgesChangeSafe : undefined}
            onConnect={onConnect}
            connectionMode={ConnectionMode.Loose}
            onNodeDragStart={mode === 'edit' ? onNodeDragStart : undefined}
            onNodeDrag={mode === 'edit' ? onNodeDrag : undefined}
            onNodeDragStop={onNodeDragStop}
            onSelectionChange={onSelectionChange}
            onNodeContextMenu={(e, node) => openKando('node', node.id, e)}
            onEdgeContextMenu={(e, edge) => openKando('edge', edge.id, e)}
            onPaneContextMenu={(e) => openKando('canvas', null, e)}
            onNodeDoubleClick={(_, node) => {
              setMode('edit');
              setEditTarget({ kind: 'node', id: node.id });
            }}
            onEdgeDoubleClick={(_, edge) => {
              setMode('edit');
              setEditTarget({ kind: 'edge', id: edge.id });
            }}
            nodesDraggable={mode === 'edit'}
            nodesConnectable={mode === 'edit'}
            elementsSelectable
            elevateNodesOnSelect={false}
            onlyRenderVisibleElements={!exporting}
            snapToGrid={mode === 'edit'}
            snapGrid={[DIAGRAM_GRID_SIZE, DIAGRAM_GRID_SIZE]}
            minZoom={0.05}
            maxZoom={2}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={(e) => {
              e.preventDefault();
              document.body.classList.remove('ukrop-palette-dragging');
              if (mode !== 'edit') return;
              const type = (e.dataTransfer.getData('application/ukrop-device') ||
                e.dataTransfer.getData('text/plain')) as DeviceType;
              if (!type || !(type in DEVICE_TYPE_LABELS_UK)) return;
              diagramStore.addDevice(
                type,
                snapPosition(screenToFlowPosition({ x: e.clientX, y: e.clientY })),
              );
              setPaletteOpen(false);
            }}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={DIAGRAM_GRID_SIZE} size={1} color="transparent" />
            <AlignGuidesOverlay guides={alignGuides} />
            <DiagramZoomControls />
            <MiniMap pannable zoomable />
          </ReactFlow>
          </EdgeRoutingProvider>
        </div>
      </div>

      <footer className="status-bar">
        <span>
          Серцебиття:{' '}
          {sim.lastHeartbeatAtMs
            ? new Date(sim.lastHeartbeatAtMs).toLocaleTimeString('uk-UA')
            : 'немає'}
        </span>
        <span>Обʼєкт: {STATUS_LABELS_UK[resolved.siteStatus]}</span>
        <span>WAN: {sim.payload?.wan.active ?? '—'}</span>
        <span>
          ЦП {sim.payload?.system.cpuLoad ?? '—'}% · Вільна ОЗП{' '}
          {sim.payload
            ? Math.round((sim.payload.system.freeMemoryBytes / 1024 / 1024) * 10) / 10
            : '—'}{' '}
          МіБ
        </span>
        <span>
          онлайн {counts.online ?? 0} · погіршено {counts.degraded ?? 0} · офлайн{' '}
          {counts.offline ?? 0} · сон {counts.sleeping ?? 0} · пасивний {counts.passive ?? 0} ·
          невідомо {counts.unknown ?? 0}
        </span>
        <span className="muted">
          ПКМ = Kando · суцільна=виміряний · штрих=виведений · сірий штрих=невідомий
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          {(Object.keys(STATUS_COLORS) as NodeStatus[]).map((s) => (
            <span className="inline-flex items-center gap-1.5 text-theme-xs text-gray-600" key={s}>
              <i
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: STATUS_COLORS[s] }}
              />
              {STATUS_LABELS_UK[s]}
            </span>
          ))}
        </div>
      </footer>

      {kando ? (
        <KandoMenu
          x={kando.x}
          y={kando.y}
          items={kandoItems}
          onSelect={onKandoSelect}
          onClose={() => setKando(null)}
        />
      ) : null}

      {editingNode ? (
        <NodeEditModal
          node={editingNode}
          disabled={mode !== 'edit'}
          onClose={() => setEditTarget(null)}
          onSave={(patch) => {
            diagramStore.updateNodes((list) =>
              list.map((n) => {
                if (n.id !== editingNode.id) return n;
                const next = { ...n, ...patch };
                if (!patch.color) delete next.color;
                if (!deviceSupportsPorts(next.type)) {
                  delete next.portCount;
                  delete next.portLayout;
                  delete next.portProfileId;
                } else {
                  if (patch.interfaces !== undefined) {
                    next.interfaces = patch.interfaces;
                    delete next.portProfileId;
                  }
                  if (patch.portProfileId !== undefined && patch.interfaces === undefined) {
                    next.portProfileId = patch.portProfileId || undefined;
                    if (!next.portProfileId) delete next.portProfileId;
                  }
                  next.portCount = resolvePortCount(next);
                  next.portLayout = resolvePortLayout(next);
                }
                if (!deviceSupportsWifiAntennas(next.type) && !deviceSupportsPorts(next.type)) {
                  delete next.interfaces;
                }
                if (!deviceSupportsWifiAntennas(next.type)) {
                  delete next.wifiAntennaCount;
                  if (next.interfaces) {
                    next.interfaces = {
                      ...next.interfaces,
                      hasWifi: false,
                      wifiCount: 0,
                      wifi24: false,
                      wifi58: false,
                    };
                  }
                } else {
                  if (patch.interfaces !== undefined) next.interfaces = patch.interfaces;
                  next.wifiAntennaCount = resolveWifiAntennaCount(next);
                  if (next.wifiAntennaCount === 0) delete next.wifiAntennaCount;
                }
                if (next.type !== 'zone') {
                  next.size = suggestSizeForPorts(
                    resolvePortCount(next),
                    next.size,
                    resolvePortLayout(next),
                    resolveWifiAntennaCount(next),
                  );
                }
                return next;
              }),
            );
            diagramStore.saveExplicit();
            setEditTarget(null);
          }}
        />
      ) : null}

      {editingEdge ? (
        <EdgeEditModal
          edge={editingEdge}
          nodes={diagram.nodes}
          edges={diagram.edges}
          disabled={mode !== 'edit'}
          onClose={() => setEditTarget(null)}
          onSave={(patch) => {
            const sourcePort = patch.sourcePort
              ? displayPortId(patch.sourcePort)
              : ETHERNET_PORT_ID;
            const targetPort = patch.targetPort
              ? displayPortId(patch.targetPort)
              : ETHERNET_PORT_ID;
            const wifiLink = isWifiPortId(sourcePort) || isWifiPortId(targetPort);
            diagramStore.updateEdges((list) =>
              list.map((e) => {
                if (e.id !== editingEdge.id) return e;
                const next = { ...e, ...patch, sourcePort, targetPort };
                if (!patch.color) delete next.color;
                if (!patch.networkCidr) delete next.networkCidr;
                if (wifiLink && (!patch.connectionType || patch.connectionType === 'ethernet')) {
                  next.connectionType = 'wifi';
                }
                return next;
              }),
            );
            updateNodeInternals(editingEdge.sourceNodeId);
            updateNodeInternals(editingEdge.targetNodeId);
            diagramStore.saveExplicit();
            setEditTarget(null);
          }}
        />
      ) : null}

      {infoNode || infoEdge ? (
        <DeviceInfoModal
          node={infoNode}
          edge={infoEdge}
          status={infoResolvedNode?.status}
          evidence={infoResolvedNode?.evidence}
          target={infoTargetTelemetry}
          iface={infoIface}
          payload={sim.payload}
          canEdit={mode === 'edit'}
          onClose={() => setInfoTarget(null)}
          onEdit={() => {
            setMode('edit');
            if (infoTarget) setEditTarget(infoTarget);
            setInfoTarget(null);
          }}
          onRefreshLook={
            mode === 'edit' && infoNode
              ? () => {
                  const look = refreshAppearanceFromNameModel({
                    label: infoNode.label,
                    model: infoNode.inventory?.model,
                    vendor: infoNode.inventory?.vendor || undefined,
                    macLocal: infoNode.inventory?.macLocal,
                    type: infoNode.type,
                  });
                  const isStarlink = look.id === 'starlink';
                  diagramStore.updateNodes((list) =>
                    list.map((n) =>
                      n.id === infoNode.id
                        ? {
                            ...n,
                            type: isStarlink ? 'satellite' : n.type,
                            inventory: {
                              ...(n.inventory ?? {}),
                              photoDataUrl: undefined,
                              appearanceId: look.id,
                              vendor:
                                isStarlink && !n.inventory?.vendor?.trim()
                                  ? 'Starlink'
                                  : n.inventory?.vendor,
                              model:
                                n.inventory?.model?.trim() ||
                                (isStarlink ? infoNode.label : n.inventory?.model),
                            },
                          }
                        : n,
                    ),
                  );
                }
              : undefined
          }
        />
      ) : null}

      <SaveProjectModal
        open={saveModalOpen}
        busy={saveBusy}
        initial={{
          name: diagram.name,
          lifecycle: normalizeNetworkLifecycle(diagram.lifecycle),
          purposeGoal: saveMeta.purposeGoal,
          purposeAudience: saveMeta.purposeAudience,
          purposeScope: saveMeta.purposeScope,
        }}
        onCancel={() => setSaveModalOpen(false)}
        onConfirm={confirmSave}
      />

      <DrawingMetaModal
        open={drawingMetaOpen}
        initial={saveMeta}
        diagram={diagram}
        onCancel={() => {
          setDrawingMetaOpen(false);
          setPendingExport(null);
        }}
        onConfirm={(meta) => {
          const kind = pendingExport;
          setDrawingMetaOpen(false);
          setPendingExport(null);
          if (kind) void runExport(kind, meta);
        }}
      />

      {versionsOpen ? (
        <div className="modal-root" role="dialog" aria-modal="true" aria-labelledby="versions-title">
          <button
            type="button"
            className="modal-backdrop"
            aria-label="Закрити"
            onClick={() => setVersionsOpen(false)}
          />
          <div className="modal-panel">
            <div className="modal-header">
              <h3 id="versions-title">Версії схеми (≤20)</h3>
              <button type="button" className="btn-sm" onClick={() => setVersionsOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <ul className="space-y-2 text-sm text-gray-600">
                {versions.length === 0 ? (
                  <li className="text-gray-400">Ще немає збережених версій.</li>
                ) : (
                  versions.map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-2">
                      <span>{new Date(v.savedAt).toLocaleString('uk-UA')}</span>
                      <button
                        type="button"
                        className="btn-sm"
                        onClick={() => {
                          diagramStore.restoreVersion(v.id);
                          setVersionsOpen(false);
                        }}
                      >
                        Відновити
                      </button>
                    </li>
                  ))
                )}
              </ul>
              <h4 className="mt-4 mb-2 text-sm font-semibold text-gray-800">Події симулятора</h4>
              <ul className="space-y-1 text-theme-xs text-gray-500">
                {sim.events.slice(-12).map((e, i) => (
                  <li key={`${e}-${i}`}>{e}</li>
                ))}
              </ul>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-sm" onClick={() => setVersionsOpen(false)}>
                Закрити
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function DiagramEditor({ initialMode }: { initialMode: 'edit' | 'monitor' }) {
  const [mode, setMode] = useState<'edit' | 'monitor'>(initialMode);
  const [sim, setSim] = useState<SimulatorState>(() => buildScenario('normal'));

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  return (
    <ReactFlowProvider>
      <PortHoverProvider>
        <EditorInner mode={mode} setMode={setMode} sim={sim} setSim={setSim} />
      </PortHoverProvider>
    </ReactFlowProvider>
  );
}
